import { getPreloadImageLink } from '@modules/images';
import {
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    fallbackOGSocialImage,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import type { MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createProductSchema } from '@modules/seo/schema/product-schema';
import { createLDSchema } from '@modules/seo/util/create-ld-schema';
import createProductOGTags from '@modules/product/util/create-product-og-tags';
import type { loader as rootLoader } from '~/root';
import WarningBanner from '@modules/root/component/warning-banner/WarningBanner';
import Variants from '@modules/product/component/variants/Variants';
import ProductDetails from '@modules/product/component/product-details/ProductDetails';
import {
    VariantSelectionProvider,
    useVariantSelectionState,
} from '@modules/product/context/VariantSelectionContext';
import PageHeader from '@modules/product/component/page-header/PageHeader';
import ProductContainer from '@modules/product/component/product-container/ProductContainer';
import AboutSeller from '@modules/product/component/about-seller/AboutSeller';
import type {
    GetFeaturedProjectsQuery,
    GetFeaturedProjectsQueryVariables,
    GetProductMainQuery,
    GetProductMainQueryVariables,
    GetProductShippingQuery,
    GetProductShippingQueryVariables,
    GetProductVariantsProfessionalPricingQuery,
    GetProductVariantsProfessionalPricingQueryVariables,
    GetRegionsQuery,
    GetRegionsQueryVariables,
    GetShoppingCartSummaryQuery,
    GetShoppingCartSummaryQueryVariables,
} from 'generated/graphql';
import {
    GetFeaturedProjectsDocument,
    GetProductMainDocument,
    GetProductShippingDocument,
    GetProductVariantsProfessionalPricingDocument,
    GetRegionsDocument,
    GetShoppingCartSummaryDocument,
    PermissionEnum,
} from 'generated/graphql';
import type { LoaderFunctionArgs, SerializeFrom } from '@remix-run/node';
import invariant from 'tiny-invariant';
import { useLoaderData } from '@remix-run/react';
import {
    Flex,
    Grid,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { VariantOptionProvider } from '@modules/product/context/VariantOptionContext';
import ProductGalleryMobile from '@modules/product/component/photo-gallery/ProductGallery.mobile';
import ProductGalleryDesktop from '~/modules/product/component/photo-gallery/ProductGallery.desktop';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import {
    assertResponse,
    response404,
} from '~/modules/root/graphql/assertResponse.server';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { extractBranchFromNetwork } from '@modules/enquiry/util/extract-branch-from-network.server';
import { getPermissions, useUser } from '@modules/user';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import { projectTileItemViewModel } from '@modules/product/util/project-tile-item-view-model.server';
import SampleEnquiryPopup from '@modules/product/component/sample-enquiry-popup/SampleEnquiryPopup';
import { useBoolean } from 'ahooks';
import type { APHandle } from '@modules/root';
import { useHydrated } from 'remix-utils';
import { hasFeature } from '@modules/root';
import { getPrimaryCTA } from '@modules/professional/util/professional-cta-helper';
import { contentWarningMessages } from '@modules/root/util/content-warning-messages';
import { processProduct } from '~/modules/product/util/process-product.server';
import { processCart } from '~/modules/product/util/process-cart.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import TradeOnlyBanner from '~/modules/product/component/trade-only-banner/TradeOnlyBanner';
import type { loader as appLoader } from '~/routes/_app';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { User } from '~/server/types';
import { getSession } from '~/modules/root/util/session.server';
import { useRef, useState } from 'react';
import ProductRange from '@modules/product/component/product-range/ProductRange';
import FeaturedProjectTiles from '@modules/product/component/featured-projects/FeaturedProjects';
import AlsoFromProducts from '@modules/product/component/also-from-products/AlsoFromProducts';
import ProductInstallers from '~/modules/product/component/product-installers/ProductInstallers';
import StickyAction from '@modules/directory/component/StickyAction';
import EnquirePurchaseStickyAction from '@modules/product/component/variant-purchase/EnquirePurchaseStickyAction';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { getProfessionalLocations } from '~/modules/product/util/get-professional-locations';
import DisabledAccount from '@modules/professional/component/disabled-account/DisabledAccount';
import { useEnquiryModalByUrl } from '~/modules/enquiry/hook/use-enquiry-modal-by-url';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const fallbackProductTitle =
            data?.product?.Professional?.Title &&
            data?.product?.Title &&
            `${data.product.Professional.Title} ${data.product.Title}`;

        const title = createMetaTitle(props, [
            data?.localisation?.DirectoryHeader?.Title,
            fallbackProductTitle,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.product?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.product?.LibraryImages?.[0]?.LibraryImageFile
                ?.DetailPageSquare?.Desktop
        );

        const productSchema = createProductSchema(props);
        const ldSchema = createLDSchema([productSchema]);
        // ToDo: Video schema

        const preloadImages = prefetchProductImages(props);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.product?.FacebookPreviewImage ?? fallbackSocialImage,
            }),
            ...createProductOGTags(
                data?.product,
                data?.shoppingCartSummary?.Currency
            ),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.product?.Professional?.Country,
            }),
            ...ldSchema,
            ...preloadImages,
            ...noIndexCrossCountry(
                data?.product?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation
            ),
        ];
    }
);

export const links = () => [
    {
        rel: 'stylesheet',
        href: '/assets/website/styles/swiper@9.1.0/modules/free-mode.min.css',
    },
];

const prefetchProductImages: MetaFunction<
    typeof loader,
    { root: typeof rootLoader; 'routes/_app': typeof appLoader }
> = (props) => {
    const { data, matches } = props;
    const { appData } = getRouteData(matches);
    const imageConfigProps = appData?.imageConfigProps;
    if (!data?.product) {
        return [];
    }

    const defaultImage =
        data.product.DefaultImage?.LibraryImageFile?.DetailPageSquare;
    const { Desktop, Mobile } = defaultImage ?? {};

    if (!data?.product) return [];

    if (!Desktop) {
        return [];
    }

    if (Mobile && data.deviceType === 'mobile') {
        return [
            ...getPreloadImageLink(
                imageConfigProps,
                Mobile,
                undefined,
                getImagesSizes({ desktop: 780, mobile: 375 })
            ),
        ];
    }

    return [
        ...getPreloadImageLink(
            imageConfigProps,
            Desktop,
            undefined,
            getImagesSizes(780)
        ),
    ];
};

export const handle: APHandle<ProductPageLoaderData> = {
    TopNavAfterRender: ({ data }) => {
        return (
            <>
                {data?.product?.Professional.Disable ? (
                    <DisabledAccount />
                ) : null}
                {data.warningMessages && (
                    <WarningBanner alerts={data.warningMessages} />
                )}
            </>
        );
    },
    fadeTopNavAfterPosition: 200,
};

export const shouldRevalidate: ShouldRevalidateFunction = ({ formAction }) => {
    return !formAction?.includes('add-to-shopping-cart');
};

const DESKTOP_SECTION_GAP = 'space-240';
const MOBILE_SECTION_GAP = 'space-80';

export const loader = async (args: LoaderFunctionArgs) => {
    const { request, params, context } = args;
    invariant(params.urlSegment, 'A product URL segment is required');

    const searchParams = new URL(request.url).searchParams;
    const productPromise = context.client.query<
        GetProductMainQuery,
        GetProductMainQueryVariables
    >({
        query: GetProductMainDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productVariantsProfessionalPricingPromise = context.client.query<
        GetProductVariantsProfessionalPricingQuery,
        GetProductVariantsProfessionalPricingQueryVariables
    >({
        query: GetProductVariantsProfessionalPricingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productShippingPromise = context.client.query<
        GetProductShippingQuery,
        GetProductShippingQueryVariables
    >({
        query: GetProductShippingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });
    const cartSummaryPromise = context.client.query<
        GetShoppingCartSummaryQuery,
        GetShoppingCartSummaryQueryVariables
    >({
        query: GetShoppingCartSummaryDocument,
    });

    const regionPromise = context.client.query<
        GetRegionsQuery,
        GetRegionsQueryVariables
    >({
        query: GetRegionsDocument,
        variables: {
            country: context.localisation.country,
            public: true,
        },
    });

    const getFeaturedProjectsLimit = (
        searchParams: URLSearchParams
    ): number => {
        return Number(searchParams.get('featuredProjectsShowAll') || 3);
    };

    const featuredProjectsPromise = context.client.query<
        GetFeaturedProjectsQuery,
        GetFeaturedProjectsQueryVariables
    >({
        query: GetFeaturedProjectsDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
            limit: getFeaturedProjectsLimit(new URL(request.url).searchParams),
        },
    });

    const localisationPromise = getPageLocalisation(args);

    const [
        user,
        productResult,
        productVariantsProfessionalPricingResult,
        productShippingResult,
        cartSummaryResult,
        regionResult,
        featuredProjectsResult,
        localisation,
    ] = await Promise.all([
        context.getUser(),
        productPromise,
        productVariantsProfessionalPricingPromise,
        productShippingPromise,
        cartSummaryPromise,
        regionPromise,
        featuredProjectsPromise,
        localisationPromise,
    ]);
    const ecommerceEnabled = hasFeature(user.FeatureFlags, 'ecommerce');
    const stickyPurchaseActionEnabled = hasFeature(
        user.FeatureFlags,
        'sticky_mobile_cta'
    );

    unsafeVariant_DO_NOT_USE(productResult);
    unsafeVariant_DO_NOT_USE(productVariantsProfessionalPricingResult);
    unsafeVariant_DO_NOT_USE(productShippingResult);
    unsafeVariant_DO_NOT_USE(cartSummaryResult);
    unsafeVariant_DO_NOT_USE(regionResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);

    const product = await assertResponse(
        args.context,
        productResult.data.Directory.Product,
        productResult.error
    );

    const productShipping = await assertResponse(
        args.context,
        productShippingResult.data.Directory.Product,
        productShippingResult.error
    );

    const productVariantsProfessionalPricing = await assertResponse(
        args.context,
        productVariantsProfessionalPricingResult.data.Directory.Product,
        productVariantsProfessionalPricingResult.error
    );

    const cartSummary = await assertResponse(
        args.context,
        cartSummaryResult.data.ShoppingCart,
        cartSummaryResult.error
    );
    invariant(cartSummary, 'Failed to load cart');

    const regions = regionResult.data.Regions;
    invariant(regions, 'Failed to load regions');

    const featuredProjects = await assertResponse(
        args.context,
        featuredProjectsResult.data.Directory.Product,
        featuredProjectsResult.error
    );

    if (!productVariantsProfessionalPricing.Professional) {
        throw await response404(args.context);
    }

    const networks = productVariantsProfessionalPricing.Professional.Networks;
    const branchOptions = extractBranchFromNetwork(networks);

    const processedProduct = processProduct(
        product,
        productVariantsProfessionalPricing,
        productShipping,
        user,
        request
    );

    const processedCart = processCart(
        cartSummary,
        processedProduct.Professional
    );

    const productEditPath = getBMPagePath(
        processedProduct.Professional.URLSegment,
        'products',
        processedProduct.URLSegment
    );

    const detailButton = {
        professionalId: processedProduct.Professional.ID,
        editLink: productEditPath,
        shareableLink: product.Link,
        id: product.ID,
        canEdit: getPermissions(
            user,
            PermissionEnum.BmProductsEdit,
            processedProduct.Professional.ID
        ),
        canCopyLink: getPermissions(
            user,
            PermissionEnum.BmMarketingView,
            processedProduct.Professional.ID
        ),
    };

    const processedFeaturedProjects = {
        FeaturedProjects: {
            pageInfo: featuredProjects.FeaturedProjects.pageInfo,
            edges: featuredProjects.FeaturedProjects.edges.map((edge) => {
                return {
                    node: projectTileItemViewModel(edge.node, args, user),
                };
            }),
        },
    };

    const showSampleEnquiry =
        searchParams.get('sample') === '2' && product.SamplesAvailable;

    const primaryCTA = getPrimaryCTA({
        primaryCTA: processedProduct.Professional.PrimaryCTA,
        pageType: 'product',
        isManufacturer: processedProduct.Professional.IsManufacturer,
    });

    // Permissions granted
    const isGranted = {
        editProduct: getPermissions(
            user,
            PermissionEnum.BmProductsEdit,
            processedProduct.Professional.ID
        ),
        supportView: getPermissions(user, PermissionEnum.BmSupportView),
    };

    // Create warning message for the WarningBanner
    const warningMessages = contentWarningMessages(
        processedProduct.Professional.Country,
        context.localisation.country,
        !processedProduct.IsLive,
        'product',
        {
            showPageNotLiveWarning: isGranted.supportView,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        }
    );

    const { locations: showrooms, linkedProfiles } = getProfessionalLocations(
        product.Professional,
        ['Showroom']
    );

    const whereToBuyEnabled =
        hasFeature(user.FeatureFlags, 'show_where_to_buy_products') &&
        showrooms.length > 0;

    const session = await getSession(request);

    return json(
        {
            deviceType: context.device.type,
            isGranted,
            product: processedProduct,
            shoppingCartSummary: processedCart,
            regions: regions,
            featuredProjects: processedFeaturedProjects,
            branchOptions,
            detailButton,
            localisation,
            showSampleEnquiry,
            ecommerceEnabled,
            stickyPurchaseActionEnabled,
            primaryCTA,
            ...(warningMessages && { warningMessages }),
            whereToBuyEnabled,
            showrooms,
            linkedProfiles,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

export type ProductPageLoaderData = SerializeFrom<typeof loader>;

const WrappedTradeOnlyBanner = ({ user }: { user: User }) => {
    const { selectedVariant } = useVariantSelectionState();
    return <TradeOnlyBanner user={user} selectedVariant={selectedVariant} />;
};

const ProductPage = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
        detailButton,
        showSampleEnquiry,
        ecommerceEnabled,
        stickyPurchaseActionEnabled,
        primaryCTA,
        whereToBuyEnabled,
    } = useLoaderData<typeof loader>();

    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const user = useUser();
    const hydrated = useHydrated();

    const [enquiryModalOpen, { setFalse: closeEnquiryModal }] =
        useBoolean(showSampleEnquiry);

    const productEditPath = getBMPagePath(
        product.Professional?.URLSegment,
        'products',
        product.URLSegment
    );
    useSetEditMode(productEditPath, product.Professional?.ID);

    const hasProductRange =
        (product.ProductRange?.edges &&
            product.ProductRange?.edges.length > 0) ??
        false;

    const defaultImage =
        product.DefaultImage?.LibraryImageFile?.DetailPageSquare;

    //sections to hide the sticky action.
    const primaryCTASectionRef = useRef<HTMLDivElement>(null);
    const enquireSectionRef = useRef<HTMLDivElement>(null);
    const extrasSectionRef = useRef<HTMLDivElement>(null);

    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);

    const { renderEnquiryModal, openEnquiryModal } = useEnquiryModalByUrl();

    return (
        <ProductContainer>
            <VariantSelectionProvider product={product}>
                {!mobile && (
                    <PageHeader
                        data={{ ...product, PrimaryCTA: primaryCTA }}
                        openEnquiryModal={openEnquiryModal}
                    />
                )}
                <Flex
                    column
                    gap={mobile ? MOBILE_SECTION_GAP : DESKTOP_SECTION_GAP}
                >
                    <div>
                        <VariantOptionProvider product={product}>
                            <Grid
                                columns={mobile ? '1fr' : `${pxToRem(880)} 1fr`}
                                className={css({
                                    columnGap: pxToRem(44),
                                })}
                                design={{
                                    display: mobile ? 'block' : 'grid',
                                }}
                            >
                                {mobile ? (
                                    <ProductGalleryMobile
                                        data={product}
                                        firstSlide={defaultImage?.Mobile ?? ''}
                                        title={product.Title}
                                    />
                                ) : (
                                    <ProductGalleryDesktop
                                        data={product}
                                        firstSlide={{
                                            src: defaultImage?.Desktop ?? '',
                                            isMultiplied:
                                                product?.DefaultImage
                                                    ?.IsMultiplied ?? false,
                                        }}
                                        title={product.Title}
                                        loop={true}
                                    />
                                )}
                                {mobile && (
                                    <PageHeader
                                        data={{
                                            ...product,
                                            PrimaryCTA: primaryCTA,
                                        }}
                                        openEnquiryModal={openEnquiryModal}
                                    />
                                )}
                                <div>
                                    {!mobile && ecommerceEnabled && (
                                        <>
                                            <SpecialtyPricingBanner
                                                product={product}
                                            />
                                            <WrappedTradeOnlyBanner
                                                user={user}
                                            />
                                        </>
                                    )}
                                    <Variants
                                        data={{
                                            product: product,
                                            ecommerceEnabled,
                                        }}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                    />
                                    <ProductDetails
                                        data={{
                                            ...product,
                                            primaryCTA,
                                            primaryCTASectionRef,
                                            enquireSectionRef,
                                        }}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                    />
                                </div>
                            </Grid>
                        </VariantOptionProvider>
                    </div>

                    <div
                        ref={extrasSectionRef}
                        className={css({
                            '& > div:last-child': {
                                marginBottom: 0,
                            },
                        })}
                    >
                        <ProductRange
                            data={{
                                ProductRange: product.ProductRange,
                                Professional: product.Professional,
                                RangeTitle: product.LibraryRange?.Title ?? '',
                            }}
                        />
                        <FeaturedProjectTiles
                            data={{
                                FeaturedProjects,
                                productTitle: product.Title,
                            }}
                        />
                        <AlsoFromProducts
                            data={{
                                ...product,
                                HasRanges: hasProductRange,
                            }}
                        />
                        <ProductInstallers
                            data={{
                                ...product,
                            }}
                        />
                    </div>
                    {!mobile && <AboutSeller data={product} />}
                </Flex>
                <DetailPageButtons {...detailButton} />
                {showSampleEnquiry && hydrated && !!product.Professional && (
                    <SampleEnquiryPopup
                        data={{ product }}
                        open={enquiryModalOpen}
                        onClose={closeEnquiryModal}
                    />
                )}

                {stickyPurchaseActionEnabled && mobile && (
                    <StickyAction
                        revealBounds={[1, 3000]}
                        position={{ bottom: 0 }}
                        height={74}
                        hiddenOn={[
                            { ref: primaryCTASectionRef, threshold: 0 },
                            { ref: enquireSectionRef, threshold: 0.1 },
                            { ref: extrasSectionRef, threshold: 0 },
                        ]}
                    >
                        <EnquirePurchaseStickyAction
                            product={product}
                            primaryCTA={primaryCTA}
                        />
                    </StickyAction>
                )}
                {whereToBuyEnabled && (
                    <ProductLocationsDrawer
                        product={product}
                        open={whereToBuyOpen}
                        toggleModal={setWhereToBuyOpen}
                    />
                )}

                {renderEnquiryModal({
                    professional: {
                        ...product.Professional,
                        primaryCTA,
                        ArchiproEditorialAccount: false,
                        Website: product.Professional?.Website ?? '',
                    },
                })}
            </VariantSelectionProvider>
        </ProductContainer>
    );
};

export default ProductPage;
